// client/src/components/faculty/FacultyForm.js
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { LocaleContext } from '../../contexts/LocaleContext';
import facultyService from '../../services/facultyService';

const FacultyForm = () => {
  const { t } = useTranslation();
  const { direction } = useContext(LocaleContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    description_en: '',
    description_ar: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEditMode);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load faculty data if in edit mode
  useEffect(() => {
    if (isEditMode) {
      const loadFaculty = async () => {
        try {
          setFetchLoading(true);
          const response = await facultyService.getFaculty(id);
          setFormData(response.data);
        } catch (err) {
          setError(t('errorLoadingFaculty'));
          navigate('/faculties');
        } finally {
          setFetchLoading(false);
        }
      };
      
      loadFaculty();
    }
  }, [isEditMode, id, navigate, t]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      if (isEditMode) {
        await facultyService.updateFaculty(id, formData);
        setSuccess(t('facultyUpdatedSuccess'));
      } else {
        await facultyService.createFaculty(formData);
        setSuccess(t('facultyCreatedSuccess'));
        setFormData({
          name_en: '',
          name_ar: '',
          description_en: '',
          description_ar: ''
        });
      }
    } catch (err) {
      setError(isEditMode ? t('errorUpdatingFaculty') : t('errorCreatingFaculty'));
    } finally {
      setLoading(false);
      window.scrollTo(0, 0);
    }
  };

  if (fetchLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {isEditMode ? t('editFaculty') : t('addFaculty')}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                id="name_en"
                name="name_en"
                label={t('nameEn')}
                value={formData.name_en}
                onChange={handleChange}
                inputProps={{ dir: 'ltr' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                id="name_ar"
                name="name_ar"
                label={t('nameAr')}
                value={formData.name_ar}
                onChange={handleChange}
                inputProps={{ dir: 'rtl' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="description_en"
                name="description_en"
                label={t('descriptionEn')}
                value={formData.description_en}
                onChange={handleChange}
                multiline
                rows={4}
                inputProps={{ dir: 'ltr' }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="description_ar"
                name="description_ar"
                label={t('descriptionAr')}
                value={formData.description_ar}
                onChange={handleChange}
                multiline
                rows={4}
                inputProps={{ dir: 'rtl' }}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ 
            mt: 3, 
            display: 'flex',
            gap: 2,
            justifyContent: direction === 'ltr' ? 'flex-end' : 'flex-start'
          }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/faculties')}
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? t('saving') : t('save')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default FacultyForm;